import { syntaxTree } from "@codemirror/language";
import {
  type EditorState,
  type Extension,
  type Range,
  type Text,
} from "@codemirror/state";
import { type SyntaxNode } from "@lezer/common";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import {
  encodeTarget,
  isExternalHref,
  normalizeLinkDestination,
  parseWikiLink,
} from "../vault/links.ts";
import { markingField } from "./docHighlights.ts";

/**
 * Live Preview (Obsidian-style hybrid editing) for markdown.
 *
 * The document is NEVER modified — it stays literal `.md` text on disk and in
 * memory. This layer only *decorates* how it looks: syntax markers (`**`, `#`,
 * `> `, backticks…) are visually hidden and the spanned text is styled (bold,
 * heading sizes, inline code…). When the caret/selection sits on a line, that
 * line reveals its raw markers so you can edit them — same behavior as Obsidian.
 *
 * Because it's decoration-only, round-tripping through this editor cannot alter
 * a single byte, so compatibility with Obsidian / plain editors is perfect.
 */

const HEADING_LEVEL: Record<string, number> = {
  ATXHeading1: 1,
  ATXHeading2: 2,
  ATXHeading3: 3,
  ATXHeading4: 4,
  ATXHeading5: 5,
  ATXHeading6: 6,
};

// ── ¿Esto es realmente un link? ──────────────────────────────────────────────
// El parser de markdown (lezer) emite un nodo `Link` para CUALQUIER `[...]`,
// porque en CommonMark eso *puede* ser un "shortcut reference link". Pero solo
// lo es si existe una definición `[etiqueta]: url` en el documento. Sin ese
// chequeo, `[ asi ]` o el `[x]` de un checkbox se veían y se ocultaban como si
// fueran links — que es exactamente lo que no pasa en la vista renderizada.
// Acá se resuelve el link de verdad: o tiene URL inline, o su etiqueta apunta a
// una definición existente. Si no, no se decora nada y el texto queda literal.

/** `[etiqueta]: url` al inicio de línea (hasta 3 espacios de sangría). */
const LINK_DEF_RE = /^ {0,3}\[([^\]]+)\]:/gm;

/** Etiquetas de referencia por documento — se recalcula solo cuando cambia. */
const linkDefsCache = new WeakMap<Text, Set<string>>();

/** Normalización de etiquetas de CommonMark: sin mayúsculas ni espacios de más. */
function normalizeLabel(label: string): string {
  return label.trim().replace(/\s+/g, " ").toLowerCase();
}

function linkDefs(doc: Text): Set<string> {
  const cached = linkDefsCache.get(doc);
  if (cached) return cached;
  const defs = new Set<string>();
  for (const m of doc.toString().matchAll(LINK_DEF_RE)) defs.add(normalizeLabel(m[1]));
  linkDefsCache.set(doc, defs);
  return defs;
}

/** El tramo VISIBLE de un enlace: lo que va entre el primer `[` y su `]`. */
function labelRange(node: SyntaxNode): { from: number; to: number } | null {
  const marks: SyntaxNode[] = [];
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "LinkMark") marks.push(ch);
    if (marks.length === 2) return { from: marks[0].to, to: marks[1].from };
  }
  return null;
}

/** El texto entre los primeros `[` y `]` del nodo. */
function bracketText(node: SyntaxNode, state: EditorState): string {
  const marks: SyntaxNode[] = [];
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "LinkMark") marks.push(ch);
    if (marks.length === 2) break;
  }
  if (marks.length < 2) return "";
  return state.sliceDoc(marks[0].to, marks[1].from);
}

/** True solo si el `Link` apunta a algún lado (URL inline o referencia definida). */
function isResolvedLink(node: SyntaxNode, state: EditorState): boolean {
  let label: string | null = null;
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "URL") return true; // [texto](url)
    if (ch.name === "LinkLabel") label = state.sliceDoc(ch.from + 1, ch.to - 1);
  }
  const defs = linkDefs(state.doc);
  // `[texto][ref]`, `[texto][]` (colapsado) y `[texto]` (shortcut) resuelven
  // contra las definiciones del documento; el colapsado usa su propio texto.
  const key = label !== null && label.trim() !== "" ? label : bracketText(node, state);
  return defs.has(normalizeLabel(key));
}

/** El destino de un `Link`, ya sea inline `[t](url)` o por referencia. */
function hrefOf(node: SyntaxNode, state: EditorState): string | null {
  let label: string | null = null;
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "URL") {
      // Un destino entre `<>` es legal en markdown y lo que vale es lo de
      // adentro: es como se escribe una ruta con espacios sin escapar.
      const raw = state.sliceDoc(ch.from, ch.to).trim();
      return raw.startsWith("<") && raw.endsWith(">") ? raw.slice(1, -1) : raw;
    }
    if (ch.name === "LinkLabel") label = state.sliceDoc(ch.from + 1, ch.to - 1);
  }
  const key = normalizeLabel(
    label !== null && label.trim() !== "" ? label : bracketText(node, state),
  );
  if (!key) return null;
  for (const m of state.doc.toString().matchAll(/^ {0,3}\[([^\]]+)\]:[ \t]*(\S+)/gm)) {
    if (normalizeLabel(m[1]) === key) return m[2];
  }
  return null;
}

/** Un enlace del documento: dónde empieza y termina, qué dice y a dónde va. */
export interface LinkSpan {
  from: number;
  to: number;
  /** El texto visible. */
  text: string;
  /** El destino tal cual está escrito en el `.md`. */
  href: string;
  /** `[[wikilink]]` de un vault importado (se lee, nunca se escribe). */
  wiki: boolean;
}

/**
 * El enlace que hay en `pos`, o null si ahí no hay ninguno.
 *
 * Es la única puerta por la que se toca un enlace: el click que navega, la
 * ficha del hover y el comando que lo edita salen todos de acá. Vive en este
 * módulo porque acá está el criterio de qué `[...]` es un enlace de verdad
 * (`isResolvedLink`), y los tres tienen que coincidir: decorar algo como enlace
 * y después no poder navegarlo sería peor que no decorarlo.
 */
export function linkAt(state: EditorState, pos: number): LinkSpan | null {
  for (let node: SyntaxNode | null = syntaxTree(state).resolveInner(pos, 1); node; node = node.parent) {
    if (node.name !== "Link") continue;
    if (!isResolvedLink(node, state)) return null;
    const href = hrefOf(node, state);
    if (href === null) return null;
    return { from: node.from, to: node.to, text: bracketText(node, state), href, wiki: false };
  }
  // Los wikilinks no están en el árbol: se buscan en la línea.
  const line = state.doc.lineAt(pos);
  for (const m of line.text.matchAll(WIKI_RE)) {
    const from = line.from + (m.index ?? 0);
    const to = from + m[0].length;
    if (pos < from || pos > to) continue;
    const { target, label } = parseWikiLink(m[1]);
    return { from, to, text: label, href: encodeTarget(target), wiki: true };
  }
  return null;
}

// ── Imágenes ─────────────────────────────────────────────────────────────────

export interface LivePreviewOptions {
  /**
   * Resuelve el `src` de una imagen (una ruta relativa al documento) a una URL
   * que el navegador pueda cargar, o null si el archivo no está. Es la misma
   * función que usa el panel de lectura; sin ella las imágenes quedan como texto.
   */
  resolveImage?: (src: string) => Promise<string | null>;
  /**
   * Ruta del vault a la que lleva un enlace, o null si no existe. Es síncrona
   * (contesta contra el árbol en memoria, ver `vault/links.ts`), así un enlace
   * roto nace roto en el decorado y nunca se ve un instante como bueno.
   */
  resolveLink?: (href: string) => string | null;
}

/**
 * Las imágenes del vault no se pueden poner directo en un `<img>`: hay que leer
 * el archivo y crear un blob URL. Como el decorado se reconstruye en cada tecla,
 * el resultado se cachea por `src` — sin cache cada pulsación releería el
 * archivo y dejaría un blob URL colgado. Se revocan todos al destruir la vista.
 */
class ImageCache {
  private urls = new Map<string, string | null>();
  private pending = new Map<string, Promise<string | null>>();
  private blobs: string[] = [];

  constructor(private resolve?: (src: string) => Promise<string | null>) {}

  /** URL ya resuelta, null si el archivo falta, undefined si todavía no se leyó. */
  get(src: string): string | null | undefined {
    return this.urls.get(src);
  }

  load(src: string): Promise<string | null> {
    const inFlight = this.pending.get(src);
    if (inFlight) return inFlight;
    const p = (this.resolve ? this.resolve(src) : Promise.resolve(null))
      .catch(() => null)
      .then((url) => {
        this.urls.set(src, url);
        if (url && /^blob:/i.test(url)) this.blobs.push(url);
        return url;
      });
    this.pending.set(src, p);
    return p;
  }

  dispose(): void {
    for (const url of this.blobs) URL.revokeObjectURL(url);
    this.blobs = [];
    this.urls.clear();
    this.pending.clear();
  }
}

interface ImageInfo {
  src: string;
  alt: string;
  width: number | null;
  height: number | null;
}

/**
 * Destino, alt y tamaño de un nodo `Image`, o null si no tiene URL inline
 * (`![alt][ref]` y demás referencias quedan como texto literal).
 *
 * El sufijo `|300` / `|300x200` del alt es el mismo que entiende la vista
 * previa (ver `imageSizes` en render/markdown.ts), así una imagen se ve del
 * mismo ancho en los dos modos.
 */
function imageInfo(node: SyntaxNode, state: EditorState): ImageInfo | null {
  let url: string | null = null;
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "URL") {
      url = state.sliceDoc(ch.from, ch.to);
      break;
    }
  }
  if (url === null) return null;
  const raw = bracketText(node, state);
  const m = /^(.*?)\|(\d+)(?:[xX×](\d+))?$/.exec(raw);
  return {
    src: url.trim().replace(/^<|>$/g, ""),
    alt: m ? m[1] : raw,
    width: m ? Number(m[2]) : null,
    height: m?.[3] ? Number(m[3]) : null,
  };
}

/** La imagen dibujada en lugar de `![alt](ruta)`. */
class ImageWidget extends WidgetType {
  constructor(private info: ImageInfo, private cache: ImageCache) {
    super();
  }
  eq(other: ImageWidget): boolean {
    const a = this.info;
    const b = other.info;
    return a.src === b.src && a.alt === b.alt && a.width === b.width && a.height === b.height;
  }
  toDOM(): HTMLElement {
    const { src, alt, width, height } = this.info;
    const img = document.createElement("img");
    img.className = "cm-lp-img";
    img.alt = alt;
    // De acá lo lee el click que abre el popup de ancho: es la ruta tal cual
    // está escrita en el `.md`, que es lo que hay que reescribir.
    img.dataset.src = src;
    if (width !== null) img.width = width;
    if (height !== null) img.height = height;
    const cached = this.cache.get(src);
    if (cached === undefined) void this.cache.load(src).then((url) => applyImageUrl(img, url));
    else applyImageUrl(img, cached);
    return img;
  }
}

function applyImageUrl(img: HTMLImageElement, url: string | null): void {
  if (url) img.src = url;
  else img.classList.add("cm-lp-img-missing");
}

/** `- [ ] ` / `- [x] ` al principio de un ítem de lista. */
const TASK_RE = /^(\s*)([-*+])(\s+)\[([ xX])\]\s/;

/**
 * Checkbox real en lugar de `[ ]` / `[x]`. Clickear alterna el carácter en el
 * documento, así que el `.md` sigue siendo texto plano portable.
 */
class TaskWidget extends WidgetType {
  constructor(private checked: boolean, private pos: number) {
    super();
  }
  eq(other: TaskWidget): boolean {
    return other.checked === this.checked && other.pos === this.pos;
  }
  toDOM(view: EditorView): HTMLElement {
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "cm-lp-task";
    box.checked = this.checked;
    box.addEventListener("mousedown", (e) => {
      e.preventDefault();
      // Con el marcador en la mano se está resaltando, no tildando.
      if (view.state.field(markingField, false)) return;
      view.dispatch({
        changes: { from: this.pos, to: this.pos + 1, insert: this.checked ? " " : "x" },
      });
    });
    return box;
  }
  ignoreEvent(): boolean {
    return false;
  }
}

/** Replaces an unordered list marker (`-`, `*`, `+`) with a real bullet. */
class BulletWidget extends WidgetType {
  eq(): boolean {
    return true;
  }
  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "cm-lp-bullet";
    span.textContent = "•";
    return span;
  }
  ignoreEvent(): boolean {
    return false;
  }
}

/** `[[nota]]` o `[[nota|texto]]`, en una sola línea. */
const WIKI_RE = /\[\[([^\[\]\n]+)\]\]/g;

interface Built {
  decorations: DecorationSet;
  /**
   * Los enlaces, como rangos ATÓMICOS: el cursor los salta enteros en vez de
   * meterse adentro, y borrar junto a uno se lleva el enlace completo. Es la
   * otra mitad de "un enlace es una pieza" —sin esto se podría editar por
   * dentro un texto cuyo destino está oculto, que es la peor de las dos cosas.
   */
  atomic: DecorationSet;
}

function buildDecorations(
  view: EditorView,
  images: ImageCache,
  opts: LivePreviewOptions,
): Built {
  const { state } = view;
  const decos: Range<Decoration>[] = [];
  const links: Array<{ from: number; to: number }> = [];
  const tree = syntaxTree(state);

  // Tramos de código: un `[[algo]]` ahí adentro es texto, no un enlace.
  const codeSpans: Array<{ from: number; to: number }> = [];

  /** Un enlace interno que no lleva a ningún lado se marca como roto. */
  const linkClass = (href: string | null): string => {
    if (!href || href.startsWith("#") || opts.resolveLink?.(href)) return "cm-lp-link";
    return isExternalHref(normalizeLinkDestination(href))
      ? "cm-lp-link"
      : "cm-lp-link cm-lp-link-missing";
  };

  // Corchetes que son el checkbox de una tarea: no son links, aunque el parser
  // los reporte como tales.
  const taskBoxes = new Set<number>();

  // Un mismo `Link` se consulta desde el nodo y desde cada uno de sus marcadores.
  const linkKind = new Map<number, boolean>();
  const isLink = (node: SyntaxNode): boolean => {
    let known = linkKind.get(node.from);
    if (known === undefined) {
      known = !taskBoxes.has(node.from) && isResolvedLink(node, state);
      linkKind.set(node.from, known);
    }
    return known;
  };

  // A node is "being edited" (so we reveal its raw markers) when any selection
  // range touches one of the lines the node spans.
  // Con el marcador en la mano se está leyendo, no editando: el marcado crudo
  // no vuelve aunque la selección toque la línea. Si volviera, el texto se
  // movería abajo del mouse justo mientras se arrastra para marcar.
  //
  // Sin foco tampoco se revela nada: al abrir un documento el cursor está en la
  // primera línea sin que nadie lo haya puesto ahí, y esa línea aparecía con su
  // `# ` a la vista antes de que el usuario tocara una tecla. Una hoja recién
  // abierta se lee entera.
  const quiet = state.field(markingField, false) || !view.hasFocus;
  const editing = (from: number, to: number): boolean => {
    if (quiet) return false;
    const startLine = state.doc.lineAt(from).number;
    const endLine = state.doc.lineAt(to).number;
    for (const r of state.selection.ranges) {
      const a = state.doc.lineAt(r.from).number;
      const b = state.doc.lineAt(r.to).number;
      if (a <= endLine && b >= startLine) return true;
    }
    return false;
  };

  /**
   * Lo mismo, pero para el marcado INLINE: se revela sólo el nodo donde está el
   * cursor, no todo lo que haya en la línea.
   *
   * Es la diferencia entre clickear en un párrafo y que no se mueva nada, o que
   * aparezcan de golpe todos los `**` y todas las `(url)` de esa línea: el texto
   * se alarga, el párrafo se reacomoda y el cursor termina un renglón más abajo
   * de donde se clickeó. El marcado de BLOQUE (el `#`, el `>`, la viñeta) sigue
   * siendo por línea: ahí no hay nada que se corra, y es lo que uno espera al
   * pararse en un encabezado.
   *
   * El rango es inclusivo en los dos extremos para que el cursor pegado al
   * final de una negrita todavía la muestre abierta y se pueda seguir
   * escribiendo adentro.
   */
  const editingNode = (node: SyntaxNode | null, from: number, to: number): boolean => {
    if (quiet) return false;
    const a = node ? node.from : from;
    const b = node ? node.to : to;
    for (const r of state.selection.ranges) if (r.from <= b && r.to >= a) return true;
    return false;
  };

  // Hide a marker range, or — while editing that line — just dim it.
  const conceal = (from: number, to: number, isEditing: boolean): void => {
    if (from >= to) return;
    decos.push(
      isEditing
        ? Decoration.mark({ class: "cm-lp-mark" }).range(from, to)
        : Decoration.replace({}).range(from, to),
    );
  };

  for (const { from, to } of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: (node) => {
        const name = node.name;

        // ── Imágenes: se dibuja la imagen en lugar de `![alt](ruta)`. Sobre la
        //    línea del cursor vuelve el marcado crudo, como cualquier otro
        //    marcador, para poder editar la ruta o el alt.
        //    Siempre se corta el descenso: los `LinkMark`/`URL` de adentro no
        //    son los de un link y no se decoran.
        if (name === "Image") {
          if (editing(node.from, node.to)) return false;
          const info = imageInfo(node.node, state);
          if (!info) return false;
          decos.push(
            Decoration.replace({ widget: new ImageWidget(info, images) }).range(node.from, node.to),
          );
          return false;
        }

        // ── Headings: size the whole line, hide the leading "### ".
        const level = HEADING_LEVEL[name];
        if (level) {
          const line = state.doc.lineAt(node.from);
          decos.push(
            Decoration.line({ class: `cm-lp-h${level}` }).range(line.from),
          );
          const m = /^#{1,6}\s+/.exec(line.text);
          if (m) conceal(line.from, line.from + m[0].length, editing(node.from, node.to));
          return;
        }

        switch (name) {
          // ── Inline emphasis: style the span…
          case "StrongEmphasis":
            decos.push(Decoration.mark({ class: "cm-lp-strong" }).range(node.from, node.to));
            break;
          case "Emphasis":
            decos.push(Decoration.mark({ class: "cm-lp-em" }).range(node.from, node.to));
            break;
          case "Strikethrough":
            decos.push(Decoration.mark({ class: "cm-lp-strike" }).range(node.from, node.to));
            break;
          case "InlineCode":
            codeSpans.push({ from: node.from, to: node.to });
            decos.push(Decoration.mark({ class: "cm-lp-code" }).range(node.from, node.to));
            break;

          // …and hide the surrounding markers (`**`, `*`, `~~`, backticks).
          case "EmphasisMark":
          case "StrikethroughMark":
          case "CodeMark":
            conceal(node.from, node.to, editingNode(node.node.parent, node.from, node.to));
            break;

          // ── Lists: turn -, *, + into a bullet (ordered markers stay).
          case "ListMark": {
            const marker = state.sliceDoc(node.from, node.to);
            const isEditing = editing(node.from, node.to);

            // Tareas: `- [ ] ` / `- [x] ` se muestran como un checkbox real
            // (marcador + corchetes juntos), no como viñeta + link falso.
            const line = state.doc.lineAt(node.from);
            const task = TASK_RE.exec(line.text);
            if (task && line.from + task[1].length === node.from) {
              const boxFrom = node.to + task[3].length;
              taskBoxes.add(boxFrom);
              if (!isEditing) {
                decos.push(
                  Decoration.replace({
                    widget: new TaskWidget(task[4] !== " ", boxFrom + 1),
                  }).range(node.from, boxFrom + 3),
                );
              } else {
                decos.push(Decoration.mark({ class: "cm-lp-mark" }).range(node.from, node.to));
              }
              break;
            }

            if (/^[-*+]$/.test(marker) && !isEditing) {
              decos.push(
                Decoration.replace({ widget: new BulletWidget() }).range(node.from, node.to),
              );
            } else if (isEditing) {
              decos.push(Decoration.mark({ class: "cm-lp-mark" }).range(node.from, node.to));
            }
            break;
          }

          // ── Blockquote: left border on the line, hide the "> ".
          case "QuoteMark": {
            const line = state.doc.lineAt(node.from);
            decos.push(Decoration.line({ class: "cm-lp-quote" }).range(line.from));
            let end = node.to;
            if (state.sliceDoc(end, end + 1) === " ") end += 1;
            conceal(node.from, end, editing(node.from, node.to));
            break;
          }

          // ── Links: show the text as an accent link, hide "[", "]", "(url)".
          //    Solo si el link resuelve de verdad (ver isResolvedLink): un
          //    `[ asi ]` suelto o el `[x]` de una tarea quedan como texto.
          case "Link":
            if (isLink(node.node)) {
              decos.push(
                Decoration.mark({ class: linkClass(hrefOf(node.node, state)) }).range(
                  node.from,
                  node.to,
                ),
              );
            }
            break;
          // Los corchetes, la URL y los `[[ ]]` no vuelven NUNCA, ni con el
          // cursor encima: un enlace es una pieza, no texto que se edita a
          // mano. Se lo toca desde su ficha (el hover) o desde el comando, que
          // es lo que hace que clickearlo pueda navegar sin ambigüedad.
          case "LinkMark":
          case "LinkLabel":
          case "URL": {
            const parent = node.node.parent;
            if (parent?.name === "Link" && isLink(parent)) {
              links.push({ from: parent.from, to: parent.to });
              // Lo que cae DENTRO del texto visible no se oculta: si el enlace
              // dice una URL (`[https://…](https://…)`, que es lo que deja
              // pegar una dirección), el parser la reporta también como `URL` y
              // sin esto el enlace se veía vacío.
              const label = labelRange(parent);
              const inside = label && node.from >= label.from && node.to <= label.to;
              if (!inside) conceal(node.from, node.to, false);
            }
            break;
          }

          // ── Línea horizontal: `---` se dibuja como la línea que es.
          case "HorizontalRule": {
            const line = state.doc.lineAt(node.from);
            decos.push(Decoration.line({ class: "cm-lp-hr" }).range(line.from));
            // El final se recorta a la línea: una decoración de plugin no puede
            // tapar un salto de línea (CodeMirror lo rechaza).
            conceal(node.from, Math.min(node.to, line.to), editing(node.from, node.to));
            break;
          }

          // ── Fenced code: monospace block background across its lines.
          case "FencedCode": {
            codeSpans.push({ from: node.from, to: node.to });
            let pos = node.from;
            while (pos <= node.to) {
              const line = state.doc.lineAt(pos);
              decos.push(Decoration.line({ class: "cm-lp-codeblock" }).range(line.from));
              if (line.to + 1 > node.to) break;
              pos = line.to + 1;
            }
            break;
          }
        }
      },
    });
  }

  // ── Wikilinks `[[nota]]` / `[[nota|texto]]` ────────────────────────────────
  // El parser no los conoce (no son markdown), así que se buscan sobre el texto
  // visible. Blanqo nunca escribe uno —ver vault/links.ts—, pero un vault que
  // viene de Obsidian los tiene y sin esto quedarían como corchetes sueltos.
  for (const { from, to } of view.visibleRanges) {
    const text = state.sliceDoc(from, to);
    for (const m of text.matchAll(WIKI_RE)) {
      const start = from + (m.index ?? 0);
      const end = start + m[0].length;
      if (codeSpans.some((c) => c.from < end && c.to > start)) continue;
      const inner = m[1];
      const pipe = inner.indexOf("|");
      const labelFrom = start + 2 + (pipe >= 0 ? pipe + 1 : 0);
      const labelTo = end - 2;
      if (labelTo <= labelFrom) continue;
      const { target } = parseWikiLink(inner);
      links.push({ from: start, to: end });
      decos.push(
        Decoration.mark({ class: linkClass(encodeTarget(target)) }).range(labelFrom, labelTo),
      );
      conceal(start, labelFrom, false);
      conceal(labelTo, end, false);
    }
  }

  // Un mismo enlace se registra una vez por marcador; se deduplica acá.
  const seen = new Set<number>();
  const atomic: Range<Decoration>[] = [];
  for (const l of links) {
    if (seen.has(l.from)) continue;
    seen.add(l.from);
    atomic.push(Decoration.mark({}).range(l.from, l.to));
  }

  return {
    decorations: Decoration.set(decos, true),
    atomic: Decoration.set(atomic, true),
  };
}

export function livePreview(opts: LivePreviewOptions = {}): Extension {
  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      atomic: DecorationSet;
      // Una cache por editor: sus blob URLs mueren con él.
      private images = new ImageCache(opts.resolveImage);
      constructor(view: EditorView) {
        const built = buildDecorations(view, this.images, opts);
        this.decorations = built.decorations;
        this.atomic = built.atomic;
      }
      update(u: ViewUpdate): void {
        if (
          u.docChanged ||
          u.viewportChanged ||
          u.selectionSet ||
          u.focusChanged ||
          u.startState.field(markingField, false) !== u.state.field(markingField, false)
        ) {
          const built = buildDecorations(u.view, this.images, opts);
          this.decorations = built.decorations;
          this.atomic = built.atomic;
        }
      }
      destroy(): void {
        this.images.dispose();
      }
    },
    { decorations: (v) => v.decorations },
  );

  return [
    plugin,
    EditorView.atomicRanges.of((view) => view.plugin(plugin)?.atomic ?? Decoration.none),
  ];
}
